import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { Quote } from '../../../../../Typescript/backend/classes';
import {
  Coverage,
  InsurancePolicy,
  Peril,
} from '../../../../../Typescript/classes';
import Typography from '@mui/material/Typography';
import LoadingButton from '@mui/lab/LoadingButton';
import { styled } from '@mui/material';
import InputAdornment from '@mui/material/InputAdornment';
import {
  displayPerilPremium,
  displayPerilLimits,
  displayPerilRate,
  getTotalRate,
  TOTAL_RATE_MODIFICATION_PERIL_NAMES,
} from './utility';
import Checkbox from '@mui/material/Checkbox';
import { useMutation } from '@apollo/client';
import { EDIT_PERILS } from '../../../../../queries';
import { useAuth } from '@calefy-inc/authentication';
import { useSnackbar } from 'notistack';
import {
  EditPerilInput,
  VagoCommercialPackBindingOptions,
} from '../../../../../gql/graphql';
import Bugsnag from '@bugsnag/browser';
import {
  StyledTable,
  StyledTr,
  StyledTd,
  StyledTh,
  StyledTbody,
  StyledThead,
} from './PremiumOverridesTable';
import { PremiumOverrideConfirmationDialog } from './PremiumOverrideConfirmationDialog';
import {
  useDownloadVagoDocumentation,
  useUsingVagoSettings,
} from '../../../../../hooks';
// import { NumberInput } from '@calefy-inc/informedMaterial';
import MaterialTextField from '@mui/material/TextField';
import FormControlLabel from '@mui/material/FormControlLabel';
import { VagoDisplayAdditionalCoverages } from './Vago';
import {
  VagoAdditionalCoveragePerilNames,
  vagoAdditionalCoveragePerilNames,
} from './Vago/constants';
import { stringToNumber } from '../../../../../util';
import { AlreadyBoundTooltip } from './AlreadyBoundTooltip';
import {
  PropertyInformation,
  SetTotalPropertyPremium,
  arePropertyInformationsDifferent,
} from './Vago/SetTotalPropertyPremium';
import { isPropertyCoverage } from './Vago/utility/isPropertyCoverage';

interface InsurancePolicyDisplayProps {
  quote: Quote;
  insurancePolicy: InsurancePolicy;
}

//This is the entire table, PerilRow is each individual row, then td inside PerilRow contains the information
export const InsurancePolicyDisplay = ({
  quote,
  insurancePolicy,
}: InsurancePolicyDisplayProps) => {
  const [perilsToEdit, setPerilsToEdit] = useState<Array<EditPerilInput>>([]);
  const [confirmationDialogOpen, setConfirmationDialogOpen] =
    useState<boolean>(false);
  const { token } = useAuth();
  const onVago = useUsingVagoSettings();
  const [additionalCoveragePerils, setAdditionalCoveragePerils] = useState<
    Array<Peril>
  >([]);
  const [propertyInformation, setPropertyInformation] = useState<
    Array<PropertyInformation>
  >([]);
  const { enqueueSnackbar } = useSnackbar();
  const [downloadVagoDocumentation, _rest] = useDownloadVagoDocumentation({
    uuid: quote.uniqueId,
    onSuccess: () =>
      enqueueSnackbar('Downloaded documents', { variant: 'success' }),
    onFailure: (error) => {
      enqueueSnackbar(`Error downloading documentation: ${error.message}`, {
        variant: 'error',
      });
    },
  });
  const [
    editPerils,
    {
      loading,
      error,
      // data
    },
  ] = useMutation(EDIT_PERILS, {
    refetchQueries: ['quoteInsurancePolicies'],
    onCompleted: (data) => {
      if (data?.editPerils) {
        enqueueSnackbar('Successfully updated', { variant: 'success' });
        setPerilsToEdit([]);
        if (downloadVagoDocumentation) {
          setTimeout(() => downloadVagoDocumentation(), 3000);
        }
      }
    },
  });

  useEffect(() => {
    if (error) {
      enqueueSnackbar('Error updating', { variant: 'error' });
      Bugsnag.notify(error);
    }
  }, [error]);

  // Separate out the vago additional coverage perils
  useEffect(() => {
    if (!onVago) {
      return;
    }
    setAdditionalCoveragePerils(
      insurancePolicy.coverages.reduce((acc: Array<Peril>, coverage) => {
        return [
          ...acc,
          ...coverage.perils
            .filter((peril) =>
              vagoAdditionalCoveragePerilNames.find(
                (additionalCoveragePerilName) =>
                  additionalCoveragePerilName === peril.name,
              ),
            )
            .map((peril) =>
              peril.name ===
              VagoAdditionalCoveragePerilNames.EQUIPMENT_BREAKDOWN
                ? peril.copyWithAmendments({
                    displayName: `Boiler - ${coverage.getAllCoveredLocationsString()}`,
                  })
                : peril,
            ),
        ];
      }, []),
    );
  }, [onVago, insurancePolicy]);

  const addPropertyInformation = useCallback(
    (newInfo: PropertyInformation) => {
      setPropertyInformation((oldPropertyInformation) => {
        const existing = oldPropertyInformation.find(
          (old) => old.id === newInfo.id,
        );
        if (existing) {
          return arePropertyInformationsDifferent(existing, newInfo)
            ? [
                ...oldPropertyInformation.filter(
                  (info) => info.id !== newInfo.id,
                ),
                // merge the existing and new
                { ...existing, ...newInfo },
              ]
            : oldPropertyInformation;
        }
        return [...oldPropertyInformation, newInfo];
      });
    },
    [setPropertyInformation],
  );

  const setPropertyInformationAdjustmentFunction = useCallback(
    ({
      id,
      updateAmount,
    }: Pick<PropertyInformation, 'id' | 'updateAmount'>) => {
      setPropertyInformation((oldPropertyInformation) => {
        const existing = oldPropertyInformation.find((old) => old.id === id);
        if (!existing) {
          return oldPropertyInformation;
        }
        return [
          ...oldPropertyInformation.filter((old) => old.id !== id),
          { ...existing, updateAmount },
        ];
      });
    },
    [setPropertyInformation],
  );

  /**
   * One more change and this becomes a separate component
   */
  const SaveButton = () => {
    if (
      insurancePolicy.coverages.length === 0 ||
      insurancePolicy.coverages.every(
        (coverage) => coverage.perils.length === 0,
      )
    ) {
      return null;
    }
    return (
      <LoadingButtonContainer>
        <LoadingButton
          variant='contained'
          loading={loading}
          disabled={!token || perilsToEdit.length === 0}
          onClick={() => {
            setConfirmationDialogOpen(true);
          }}
        >
          Save Changes
        </LoadingButton>
      </LoadingButtonContainer>
    );
  };

  return (
    <InsurancePolicyDisplayContainer>
      <Typography component='h2' variant='h4'>
        {insurancePolicy.description}
      </Typography>
      <SaveButton />
      {error ? (
        <ErrorSpan>Error updating premiums: {error.message}</ErrorSpan>
      ) : null}
      <SetTotalPropertyPremium propertyInformation={propertyInformation} />
      {insurancePolicy.coverages
        .sort((a, b) => parseInt(a.id) - parseInt(b.id))
        .map((coverage) => {
          return (
            <DisplayCoverage
              quote={quote}
              insurancePolicy={insurancePolicy}
              coverage={coverage}
              setPerilsToEdit={setPerilsToEdit}
              perilsFilter={(p) =>
                vagoAdditionalCoveragePerilNames.find(
                  (name) => p.name === name,
                ) === undefined
              }
              addPropertyInformation={
                isPropertyCoverage(coverage)
                  ? addPropertyInformation
                  : undefined
              }
              setPropertyInformationAdjustmentFunction={
                setPropertyInformationAdjustmentFunction
              }
              key={coverage.id}
            />
          );
        })}
      <VagoDisplayAdditionalCoverages
        bindings={quote.bindings}
        perils={additionalCoveragePerils}
        setPerilsToEdit={setPerilsToEdit}
      />
      <PremiumOverrideConfirmationDialog
        open={confirmationDialogOpen}
        onConfirm={() => {
          setConfirmationDialogOpen(false);
          editPerils({
            variables: {
              token,
              perils: perilsToEdit,
            },
          });
        }}
        onCancel={() => {
          setConfirmationDialogOpen(false);
        }}
      />
      <SaveButton />
    </InsurancePolicyDisplayContainer>
  );
};

const LoadingButtonContainer = styled('div')(() => {
  return {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'end',
  };
});

const InsurancePolicyDisplayContainer = styled('section')({
  width: '100%',
});

const COLUMN_HEADINGS_NO_PROPERTY = [
  'Peril',
  'Limits',
  'Rate',
  'Premium',
  'Override?',
] as const;
const COLUMN_HEADINGS_WITH_PROPERTY = [
  'Peril',
  'Limits',
  'Rate',
  'Premium',
  'Override?',
] as const;

interface DisplayCoverageProps {
  quote: Quote;
  insurancePolicy: InsurancePolicy;
  coverage: Coverage;
  setPerilsToEdit: Dispatch<SetStateAction<Array<EditPerilInput>>>;
  perilsFilter?: (p: Peril) => boolean; // an optional filter to apply to the filters to display
  displayName?: string; // an optional override to the display name
  addPropertyInformation?: (propertyInformation: PropertyInformation) => void;
  setPropertyInformationAdjustmentFunction?: (
    a: Pick<PropertyInformation, 'id' | 'updateAmount'>,
  ) => void;
}
const DisplayCoverage = ({
  quote,
  // insurancePolicy,
  setPerilsToEdit,
  coverage,
  perilsFilter,
  displayName,
  addPropertyInformation,
  setPropertyInformationAdjustmentFunction,
}: DisplayCoverageProps) => {
  const [bound, setBound] = useState<boolean>(false);
  const [perils, setPerils] = useState<Array<Peril>>([]);
  const [perilOverrides, setPerilOverrides] = useState<Array<Peril>>([]); // these are perils that are being modified from some other peril - e.g. when building changes, contents will appear here
  const onVago = useUsingVagoSettings();
  const [isThisPropertyCoverage, setIsThisPropertyCoverage] =
    useState<boolean>(false);
  const [columnHeadings, setColumnHeadings] = useState<Array<string>>([]);
  // when we get the coverage, change the perils and see if it's a property coverage
  useEffect(() => {
    setPerils([...coverage.perils]);
    setIsThisPropertyCoverage(isPropertyCoverage(coverage));
  }, [coverage]);
  // determine what the headers should be based on whether this is a property coverage or not
  useEffect(() => {
    setColumnHeadings(
      // @ts-expect-error
      isThisPropertyCoverage
        ? COLUMN_HEADINGS_WITH_PROPERTY
        : COLUMN_HEADINGS_NO_PROPERTY,
    );
  }, [isThisPropertyCoverage]);

  // change the bound status based on the coverage and the bindings
  useEffect(() => {
    if (quote && coverage) {
      setBound(
        onVago &&
          ((coverage.name === 'Property' &&
            quote.bindings.some(
              (binding) =>
                binding.matchesLabel(
                  VagoCommercialPackBindingOptions.Property,
                ) && binding.bound,
            )) ||
            (coverage.name === 'Liability' &&
              quote.bindings.some(
                (binding) =>
                  binding.matchesLabel(
                    VagoCommercialPackBindingOptions.Liability,
                  ) && binding.bound,
              ))),
      );
    }
  }, [quote, coverage, onVago]);

  // whenever we get new peril overrides, set them as ones to edit
  useEffect(() => {
    setPerilsToEdit((oldPerilsToEdit) => {
      return [
        ...oldPerilsToEdit.filter(
          (oldPeril) =>
            !perilOverrides
              .map((perilOverride) => perilOverride.id)
              .includes(oldPeril.perilId),
        ),
        ...perilOverrides.map((peril) => ({
          override: peril.override,
          perilId: peril.id,
          premium: String(peril.premium),
          rate: String(peril.rate),
        })),
      ];
    });
  }, [perilOverrides]);

  // update the property information whenever have new information
  useEffect(() => {
    if (addPropertyInformation) {
      const perilOverrideIds = perilOverrides.map((override) => override.id);
      const effectivePerils = [
        ...perils.filter((peril) => {
          const goodForFilter = perilsFilter ? perilsFilter(peril) : true;
          const notOverRidden = !perilOverrideIds.includes(peril.id);
          return goodForFilter && notOverRidden;
        }),
        ...perilOverrides,
      ];
      const total = effectivePerils.reduce((sum, peril) => {
        return sum + peril.premium;
      }, 0);
      addPropertyInformation({
        id: coverage.id,
        total,
      });
    }
  }, [coverage, addPropertyInformation, perils, perilOverrides, perilsFilter]);

  const addPerilOverrides = useCallback(
    (perils: Peril | Array<Peril>) => {
      const actualNewPerils = Array.isArray(perils) ? perils : [perils];
      const newPerilIds = actualNewPerils.map((peril) => peril.id);
      setPerilOverrides((oldOverrides) => {
        return [
          ...oldOverrides.filter(
            (oldOverride) => !newPerilIds.includes(oldOverride.id),
          ),
          ...actualNewPerils,
        ];
      });
    },
    [setPerilOverrides],
  );
  const removePerilOverrides = useCallback(
    (perils: Peril | Array<Peril>): void => {
      const actualNewPerils = Array.isArray(perils) ? perils : [perils];
      const newPerilIds = actualNewPerils.map((peril) => peril.id);
      setPerilOverrides((oldOverrides) => {
        return [
          ...oldOverrides.filter(
            (oldOverride) => !newPerilIds.includes(oldOverride.id),
          ),
        ];
      });
    },
    [setPerilOverrides],
  );

  if (coverage.perils.length === 0) {
    return null;
  }
  return (
    <DisplayCoverageContainer>
      <Typography component='h3' variant='h5' sx={{ alignSelf: 'start' }}>
        {displayName ? displayName : coverage.getDisplayName()}
      </Typography>
      {isThisPropertyCoverage ? (
        <CoverageTivRateContainer>
          <CoverageTivRate
            coverage={coverage}
            bound={bound}
            perilsFilter={perilsFilter}
            perilOverrides={perilOverrides}
            addPerilOverrides={addPerilOverrides}
            setPropertyInformationAdjustmentFunction={
              setPropertyInformationAdjustmentFunction
            }
          />
        </CoverageTivRateContainer>
      ) : null}
      <StyledTable>
        <StyledThead>
          <StyledTr>
            {columnHeadings.map((header) => (
              <StyledTh scope='col' key={header}>
                {header}
              </StyledTh>
            ))}
          </StyledTr>
        </StyledThead>
        <StyledTbody>
          {perils
            .filter(perilsFilter ? perilsFilter : () => true)
            .map((peril, _, allFilteredPerils) => {
              return (
                <PerilRow
                  coverage={coverage}
                  isProperty={isThisPropertyCoverage}
                  key={peril.id}
                  peril={peril}
                  perils={allFilteredPerils}
                  perilOverrides={perilOverrides}
                  addPerilOverrides={addPerilOverrides}
                  removePerilOverrides={removePerilOverrides}
                  setPerils={setPerils}
                  updatePerils={setPerilsToEdit}
                  bound={bound}
                />
              );
            })}
        </StyledTbody>
      </StyledTable>
    </DisplayCoverageContainer>
  );
};

interface CoverageTivRateProps {
  coverage: Coverage;
  bound: boolean;
  perilsFilter?: (p: Peril) => boolean; // an optional filter to apply to the filters to display
  perilOverrides: Array<Peril>;
  addPerilOverrides: (p: Peril | Array<Peril>) => void;
  setPropertyInformationAdjustmentFunction?: (
    a: Pick<PropertyInformation, 'id' | 'updateAmount'>,
  ) => void;
}

const CoverageTivRate = ({
  coverage,
  bound,
  perilsFilter = () => true,
  perilOverrides,
  addPerilOverrides,
  setPropertyInformationAdjustmentFunction,
}: CoverageTivRateProps) => {
  const [tiv, setTiv] = useState<number | undefined>();
  const [perils, setPerils] = useState<Array<Peril>>([]);
  const [totalPremium, setTotalPremium] = useState<number | undefined>();
  const [displayPremium, setDisplayPremium] = useState<string>('');
  const [displayTotalRate, setDisplayTotalRate] = useState<string>('');
  const [totalRate, setTotalRate] = useState<undefined | number>(); // total rate is building + sbu + flood + earthquake
  const [override, setOverride] = useState<boolean>(
    coverage.perils.every((peril) => peril.override),
  );
  const [originalOverride, setOriginalOverride] = useState<boolean>(
    coverage.perils.every((peril) => peril.override),
  );

  // set the premium from the perils
  useEffect(() => {
    const totalPremium = perils.reduce((sum, peril) => {
      return sum + peril.premium;
    }, 0);
    //console.log({ totalPremium });
    setTotalPremium(totalPremium);
    setDisplayPremium(displayPerilPremium(totalPremium));
    if (tiv) {
      setDisplayTotalRate(displayPerilRate(getTotalRate(perils, tiv) * 100));
    }
  }, [perils, tiv]);

  // set the override and tiv whenever the coverage changes
  useEffect(() => {
    setOriginalOverride(coverage.perils.every((peril) => peril.override));
    const newTiv = coverage.getTiv();
    if (newTiv) {
      setTiv(newTiv);
    }
  }, [coverage]);

  // set the actual perils from the coverage and the overrides
  useEffect(() => {
    const relevantPerils = coverage.perils.reduce(
      (perils: Array<Peril>, originalPeril) => {
        if (!perilsFilter(originalPeril)) {
          return perils;
        }
        const matched = perilOverrides.find(
          (override) => override.id === originalPeril.id,
        );
        if (matched) {
          return [...perils, matched];
        }
        return [...perils, originalPeril];
      },
      [],
    );
    setPerils(relevantPerils);
  }, [coverage, perilOverrides]);

  // set the actual rate when the display rate changes
  useEffect(() => {
    const asNumber = stringToNumber(displayTotalRate) || 0;
    if (displayTotalRate === null) {
      return;
    }
    setTotalRate(asNumber / 100);
  }, [displayTotalRate]);

  /**
   * When we get a new total premium, adjust the relevant perils by the appropriate amount to account for that
   * @param name - description
   * @returns - description
   */
  const adjustToNewTotalPremium = useCallback(
    (newTotalPremium: number) => {
      setTotalPremium(newTotalPremium);

      const { fixedPremiumPortion, adjustablePremiumPortion } = perils.reduce(
        (portions, peril) => {
          const { fixedPremiumPortion, adjustablePremiumPortion } = portions;
          if (
            // @ts-expect-error
            TOTAL_RATE_MODIFICATION_PERIL_NAMES.includes(peril.name)
          ) {
            return {
              fixedPremiumPortion,
              adjustablePremiumPortion:
                adjustablePremiumPortion + peril.premium,
            };
          }
          return {
            fixedPremiumPortion: fixedPremiumPortion + peril.premium,
            adjustablePremiumPortion,
          };
        },
        { fixedPremiumPortion: 0, adjustablePremiumPortion: 0 },
      );
      // new = fixed + k * adjustable -> k = (new - fixed) / adjustable
      const adjustmentFactor =
        (newTotalPremium - fixedPremiumPortion) / adjustablePremiumPortion;
      const perilsToOverride = perils.filter((peril) =>
        // @ts-expect-error
        TOTAL_RATE_MODIFICATION_PERIL_NAMES.includes(peril.name),
      );
      addPerilOverrides(
        perilsToOverride.map((peril) => {
          return peril.copyWithAmendments({
            rate: peril.rate * adjustmentFactor,
            override: true,
            premium: peril.premium * adjustmentFactor,
          });
        }),
      );
    },
    [perils, setTotalPremium, addPerilOverrides],
  );

  // update the property information when we have a new adjustment function
  useEffect(() => {
    if (setPropertyInformationAdjustmentFunction) {
      setPropertyInformationAdjustmentFunction({
        id: coverage.id,
        updateAmount: adjustToNewTotalPremium,
      });
    }
  }, [adjustToNewTotalPremium]);

  if (!tiv) {
    return null;
  }
  return (
    <>
      {override ? (
        <MaterialTextField
          label='Total Premium'
          fullWidth
          disabled={bound}
          variant='standard'
          value={displayPremium}
          InputProps={{
            endAdornment: <InputAdornment position='start'>$</InputAdornment>,
          }}
          onChange={(e) => {
            setDisplayPremium(e.target.value);
          }}
          onBlur={() => {
            const asNumber = stringToNumber(displayPremium);
            if (asNumber === null) {
              setTotalPremium(undefined);
              return;
            }
            adjustToNewTotalPremium(asNumber);
          }}
        />
      ) : totalPremium !== undefined ? (
        <Typography>
          Total Premium:{' '}
          {displayPerilPremium(totalPremium, { displayUnit: true })}
        </Typography>
      ) : null}
      {override ? (
        <MaterialTextField
          label='Total Rate'
          fullWidth
          disabled={bound}
          variant='standard'
          value={displayTotalRate}
          InputProps={{
            endAdornment: <InputAdornment position='end'>%</InputAdornment>,
          }}
          onChange={(e) => {
            setDisplayTotalRate(e.target.value);
          }}
          onBlur={() => {
            const asNumber = stringToNumber(displayTotalRate);
            if (asNumber === null) {
              return;
            }
            const asDecimal = asNumber / 100;
            const oldRate = tiv ? getTotalRate(perils, tiv) : null;
            if (oldRate) {
              const adjustmentFactor = asDecimal / oldRate;
              const perilsToOverride = perils.filter((peril) =>
                // @ts-expect-error
                TOTAL_RATE_MODIFICATION_PERIL_NAMES.includes(peril.name),
              );
              addPerilOverrides(
                perilsToOverride.map((peril) => {
                  return peril.copyWithAmendments({
                    rate: peril.rate * adjustmentFactor,
                    override: true,
                    premium: peril.premium * adjustmentFactor,
                  });
                }),
              );
            }
          }}
        />
      ) : totalRate !== undefined ? (
        <Typography>
          Total Rate: {displayPerilRate(totalRate, { displayUnit: true })}
        </Typography>
      ) : null}
      <AlreadyBoundTooltip bound={!!bound}>
        <FormControlLabel
          label='Override?'
          control={
            <Checkbox
              disabled={bound || originalOverride}
              checked={override}
              onChange={(e) => {
                const checked = e.target.checked;
                setOverride(checked);
              }}
            />
          }
        />
      </AlreadyBoundTooltip>
    </>
  );
};

const CoverageTivRateContainer = styled('div')(({ theme }) => {
  return {
    alignSelf: 'start',
    display: 'grid',
    gridTemplateColumns: '2fr 2fr 1fr',
    gap: theme.spacing(1),
    alignItems: 'center',
  };
});

export const DisplayCoverageContainer = styled('section')(({ theme }) => {
  return {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'end',
    marginBottom: theme.spacing(3),
    '&:last-of-type': {
      marginBottom: theme.spacing(1),
    },
  };
});

interface PerilRowProps {
  coverage?: Coverage;
  peril: Peril;
  perils?: Array<Peril>;
  setPerils?: Dispatch<SetStateAction<Array<Peril>>>;
  updatePerils: Dispatch<SetStateAction<Array<EditPerilInput>>>;
  displayName?: string; // optionally, a name to use instead of the one from the backend
  bound?: boolean;
  perilOverrides?: Array<Peril>;
  addPerilOverrides?: (peril: Peril | Array<Peril>) => void;
  removePerilOverrides?: (peril: Peril | Array<Peril>) => void;
  isProperty?: boolean; // is this peril from a property coverage of some sort?
  // basis?: number; // optionally, the basis to use for the premium (premium = rate * basis); by default it's the limit
}
//PerilRow is the row component which contains unique values for things such as limits, rate, etc.
export const PerilRow = ({
  coverage,
  peril: originalPeril,
  perils = [],
  setPerils,
  updatePerils,
  displayName,
  bound,
  perilOverrides,
  addPerilOverrides,
  removePerilOverrides,
  isProperty = false,
}: // basis,
PerilRowProps) => {
  const [displayValue, setDisplayValue] = useState<string>('');
  const [displayRate, setDisplayRate] = useState<string>('');
  const [checked, setChecked] = useState<boolean>(originalPeril.override);
  const [peril, setPeril] = useState<Peril>(originalPeril);
  const [isMatch, setIsMatch] = useState<boolean>(true);
  const [tivRate, setTivRate] = useState<undefined | number>();
  const [displayTivRate, setDisplayTivRate] = useState<string>('');
  const { token } = useAuth();
  const [hasTivRate, setHasTivRate] = useState<boolean>(false); // is this a peril with a separate TIV rate?
  // whenever we update the peril, add it to the list to update
  useEffect(() => {
    if (peril.match(originalPeril)) {
      updatePerils((oldPerils) =>
        oldPerils.filter((oldperil) => oldperil.perilId !== peril.id),
      );
      if (removePerilOverrides) {
        removePerilOverrides(peril);
      }
    } else {
      updatePerils((oldPerils) => [
        ...oldPerils.filter((oldperil) => oldperil.perilId !== peril.id),
        {
          perilId: peril.id,
          premium: String(peril.premium),
          rate: peril.rate === undefined ? undefined : String(peril.rate),
          override: peril.override,
        },
      ]);
      if (addPerilOverrides) {
        addPerilOverrides(peril);
      }
      const rateAsDecimal = peril.rate;
      // if it's the building peril we need to update contents, lor, and any misc coverages
      if (peril.name === 'Building' && Array.isArray(perils) && setPerils) {
        const relevantPerils = perils.filter((peril) => {
          return (
            ['Contents', 'Loss of Rent'].includes(peril.name) ||
            /miscellaneous - .+/i.exec(peril.name)
          );
        });
        relevantPerils.forEach((otherPeril) => {
          const otherPerilPremium = otherPeril.limit * rateAsDecimal;
          if (addPerilOverrides) {
            addPerilOverrides(
              otherPeril.copyWithAmendments({
                override: true,
                rate: rateAsDecimal,
                premium: otherPerilPremium,
              }),
            );
          }
          // setPerilOverrides &&
          //   setPerilOverrides((oldPerilOverrides) => {
          //     const newPerilOverride = otherPeril.copyWithAmendments({
          //       override: true,
          //       rate: rateAsDecimal,
          //       premium: otherPerilPremium,
          //     });
          //     return [
          //       ...oldPerilOverrides.filter(
          //         (oldPerilOverride) => oldPerilOverride.id !== otherPeril.id,
          //       ),
          //       newPerilOverride,
          //     ];
          //   });
        });
      }
    }
  }, [peril]);

  // whenever the peril changes, change the display value and rate
  useEffect(() => {
    setDisplayValue(displayPerilPremium(originalPeril.premium));
    setDisplayRate(displayPerilRate(originalPeril.rate * 100));
    setPeril(originalPeril);
    setChecked(originalPeril.override);
    setHasTivRate(!!originalPeril.name.match(/^sewer back-up$/i));
  }, [originalPeril]);

  // update the tiv rate
  useEffect(() => {
    if (isProperty && peril && coverage && hasTivRate) {
      const tiv = coverage.getTiv();
      if (tiv) {
        setTivRate(peril.premium / tiv);
      }
    }
  }, [peril, coverage, isProperty, hasTivRate]);

  // check that the peril and original peril are the same
  useEffect(() => {
    setIsMatch(peril.match(originalPeril));
  }, [peril, originalPeril]);

  // see if there's an override
  useEffect(() => {
    if (Array.isArray(perilOverrides)) {
      const matched = perilOverrides.find(
        (perilOverride) => perilOverride.id === originalPeril.id,
      );
      if (matched) {
        setPeril(matched);
        setDisplayValue(displayPerilPremium(matched.premium));
        setDisplayRate(displayPerilRate(matched.rate * 100));
        setChecked(matched.override);
      }
    }
  }, [originalPeril, perilOverrides]);

  // when we uncheck it, everything should revert to the way it was
  useEffect(() => {
    if (!checked) {
      setDisplayValue(displayPerilPremium(peril.premium));
      setDisplayRate(displayPerilRate(peril.rate * 100));
    }
  }, [checked]);

  // change the display tiv rate when the actual one changes
  useEffect(() => {
    if (tivRate === undefined) {
      setDisplayTivRate('');
    } else {
      setDisplayTivRate(displayPerilRate(tivRate * 100));
    }
  }, [tivRate]);

  return (
    <StyledTr
      key={peril.id}
      sx={
        !isMatch
          ? {
              backgroundColor: 'rgba(0, 0, 0, 0.05)',
            }
          : {}
      }
    >
      <StyledTd>
        <AlreadyBoundTooltip bound={!!bound}>
          {displayName || peril.displayName || peril.name}
        </AlreadyBoundTooltip>
      </StyledTd>
      <StyledTd>
        <AlreadyBoundTooltip bound={!!bound}>
          {displayPerilLimits(peril.limit)}
        </AlreadyBoundTooltip>
      </StyledTd>
      {isProperty && coverage ? (
        <StyledTd>
          {hasTivRate ? (
            tivRate === undefined ? null : !peril.override ? (
              displayPerilRate(tivRate, { displayUnit: true })
            ) : (
              <MaterialTextField
                fullWidth
                disabled={bound}
                variant='standard'
                value={displayTivRate}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position='end'>%</InputAdornment>
                  ),
                }}
                onChange={(e) => {
                  setDisplayTivRate(e.target.value);
                }}
                onBlur={() => {
                  const asPercent = stringToNumber(displayTivRate) || 0;
                  const tiv = coverage.getTiv() || 0;
                  if (asPercent === null && tiv) {
                    setDisplayTivRate(displayPerilRate(peril.premium / tiv));
                  } else {
                    const asDecimal = asPercent / 100;
                    const newPremium = asDecimal * tiv;
                    setDisplayValue(displayPerilPremium(newPremium));
                    const newRate = newPremium / peril.getBasis();
                    setDisplayRate(displayPerilRate(newRate * 100));
                    setPeril(
                      peril.copyWithAmendments({
                        rate: asDecimal,
                        premium: newPremium,
                      }),
                    );
                    if (removePerilOverrides) {
                      removePerilOverrides(peril);
                    }
                  }
                }}
              />
            )
          ) : (
            <AlreadyBoundTooltip bound={!!bound}>
              {peril.override ? (
                <MaterialTextField
                  fullWidth
                  disabled={bound}
                  variant='standard'
                  value={displayRate}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position='end'>%</InputAdornment>
                    ),
                  }}
                  onChange={(e) => {
                    setDisplayRate(e.target.value);
                  }}
                  onBlur={() => {
                    const asPercent = stringToNumber(displayRate);
                    if (asPercent === null) {
                      setDisplayRate(displayPerilRate(peril.rate));
                    } else {
                      const asDecimal = asPercent / 100;
                      const newPremium = peril.getBasis() * asDecimal;
                      setDisplayRate(displayPerilRate(asPercent));
                      setDisplayValue(displayPerilPremium(newPremium));
                      setPeril(
                        peril.copyWithAmendments({
                          rate: asDecimal,
                          premium: newPremium,
                        }),
                      );
                      if (removePerilOverrides) {
                        removePerilOverrides(peril);
                      }
                    }
                  }}
                />
              ) : (
                displayPerilRate(peril.rate, { displayUnit: true })
              )}
            </AlreadyBoundTooltip>
          )}
        </StyledTd>
      ) : (
        <StyledTd>
          <AlreadyBoundTooltip bound={!!bound}>
            {peril.override ? (
              <MaterialTextField
                fullWidth
                disabled={bound}
                variant='standard'
                value={displayRate}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position='end'>%</InputAdornment>
                  ),
                }}
                onChange={(e) => {
                  setDisplayRate(e.target.value);
                }}
                onBlur={() => {
                  const asPercent = stringToNumber(displayRate);
                  if (asPercent === null) {
                    setDisplayRate(displayPerilRate(peril.rate));
                  } else {
                    const asDecimal = asPercent / 100;
                    const newPremium = peril.getBasis() * asDecimal;
                    setDisplayRate(displayPerilRate(asPercent));
                    setDisplayValue(displayPerilPremium(newPremium));
                    if (hasTivRate && coverage) {
                      const tiv = coverage.getTiv();
                      if (tiv !== 0 && tiv !== undefined) {
                        const newDisplayTivRate = displayPerilRate(
                          newPremium / tiv,
                        );
                        setDisplayTivRate(newDisplayTivRate);
                      }
                    }
                    setPeril(
                      peril.copyWithAmendments({
                        rate: asDecimal,
                        premium: newPremium,
                      }),
                    );
                    if (removePerilOverrides) {
                      removePerilOverrides(peril);
                    }
                  }
                }}
              />
            ) : (
              displayPerilRate(peril.rate, { displayUnit: true })
            )}
          </AlreadyBoundTooltip>
        </StyledTd>
      )}
      <StyledTd>
        <AlreadyBoundTooltip bound={!!bound}>
          {peril.override ? (
            <MaterialTextField
              fullWidth
              disabled={bound}
              variant='standard'
              value={displayValue}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>$</InputAdornment>
                ),
              }}
              onChange={(e) => {
                setDisplayValue(e.target.value);
              }}
              onBlur={() => {
                const converted = stringToNumber(displayValue);
                if (converted === null) {
                  setDisplayValue(displayPerilPremium(peril.premium));
                } else {
                  const newRateAsDecimal = converted / peril.getBasis();
                  const newRateAsPercent = newRateAsDecimal * 100;
                  setDisplayValue(displayPerilPremium(converted));
                  setDisplayRate(displayPerilRate(newRateAsPercent));
                  setPeril(
                    peril.copyWithAmendments({
                      premium: converted,
                      rate: newRateAsDecimal,
                    }),
                  );
                  if (removePerilOverrides) {
                    removePerilOverrides(peril);
                  }
                }
              }}
            />
          ) : (
            displayPerilPremium(peril.premium, { displayUnit: true })
          )}
        </AlreadyBoundTooltip>
      </StyledTd>
      <StyledTd>
        <AlreadyBoundTooltip bound={!!bound}>
          <Checkbox
            disabled={bound || !token || originalPeril.override}
            checked={checked}
            onChange={(e) => {
              const checked = e.target.checked;
              setChecked(checked);
              setPeril((oldPeril) => {
                if (checked) {
                  return oldPeril.copyWithAmendments({
                    override: checked,
                  });
                } else {
                  return originalPeril.copy();
                }
              });
              if (removePerilOverrides) {
                removePerilOverrides(peril);
              }
            }}
          />
        </AlreadyBoundTooltip>
      </StyledTd>
    </StyledTr>
  );
};

const ErrorSpan = styled('span')(({ theme }) => {
  return {
    color: theme.palette.error.main,
  };
});
